import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api from '../lib/api';
import { useQueryClient } from '@tanstack/react-query';

// Globalni AI copilot. OKIDAČ JE NA BACKENDU: kad se bilješka spremi (POST /leads/contact-log),
// server u pozadini pokrene AI analizu — neovisno o frontendu. Ovdje frontend samo POLLA svježe
// prijedloge (/leads/notes/pending) i prikaže globalni popup (preživi navigaciju).

const Ctx = createContext(null);
export const useNoteAnalysis = () => useContext(Ctx) || { requestAnalysis: () => {} };

export function NoteAnalysisProvider({ children }) {
  const queryClient = useQueryClient();
  const [queue, setQueue] = useState([]);            // [{id, company_name, suggestions}]
  const [statusConfig, setStatusConfig] = useState([]);
  const [toast, setToast] = useState(null);          // {type:'loading'|'success'|'error', company, count, _seq}
  const shownRef = useRef(new Set());                // id-ovi već prikazani (da popup ne ponavlja)
  const seqRef = useRef(0);
  // VIŠE KARTICA: regruterke drže otvorenu karticu po poslodavcu. Odluka (odobri/odbij) u JEDNOJ
  // kartici mora odmah zatvoriti isti popup u OSTALIMA — inače tamo ostaje "zombi" popup.
  const bcRef = useRef(null);
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel('lead-note-ai');
    bc.onmessage = (ev) => {
      const m = ev.data || {};
      if (m.type === 'decided' && m.id != null) setQueue(q => q.filter(x => x.id !== m.id));
    };
    bcRef.current = bc;
    return () => { bc.close(); bcRef.current = null; };
  }, []);
  const broadcastDecided = useCallback((id) => { try { bcRef.current?.postMessage({ type: 'decided', id }); } catch { /* tiho */ } }, []);

  const showToast = useCallback((t, ms) => {
    const my = ++seqRef.current;
    setToast({ ...t, _seq: my });
    if (ms) setTimeout(() => setToast(cur => (cur && cur._seq === my) ? null : cur), ms);
  }, []);

  // Odobravanje prijedloga — OPTIMISTIČNO: popup nestaje odmah, primjena u pozadini + toast.
  // Tako je za regrutera spremanje trenutno, a potvrda jasna ("Primijenjeno za {firma}").
  const applyOptimistic = useCallback(async (item, approved) => {
    setQueue(q => q.filter(x => x.id !== item.id));        // makni odmah
    broadcastDecided(item.id);                              // …i u ostalim karticama
    showToast({ type: 'loading', company: item.company_name });
    try {
      const { data } = await api.post('/leads/notes/apply', { suggestion_id: item.id, approved });
      const n = Array.isArray(data?.applied) ? data.applied.length : approved.length;
      showToast({ type: 'success', company: item.company_name, count: n }, 3500);
      window.dispatchEvent(new CustomEvent('note-applied', { detail: { company: item.company_name } }));
      // Osvježi keširane statuse/CRM listu na SVIM stranicama (CRM stranica drži statuse u React Query
      // kešu i nije slušala 'note-applied' → status se vidio tek nakon refresha)
      try {
        queryClient.invalidateQueries({ queryKey: ['company-status'] });
        queryClient.invalidateQueries({ queryKey: ['crm-companies'] });
        queryClient.invalidateQueries({ queryKey: ['assignments'] });
      } catch (e) { /* tiho */ }
    } catch (e) {
      setQueue(q => [item, ...q.filter(x => x.id !== item.id)]);   // vrati u red za ponovni pokušaj
      showToast({ type: 'error', company: item.company_name }, 6000);
    }
  }, [showToast, queryClient, broadcastDecided]);

  useEffect(() => {
    api.get('/leads/status-config').then(r => setStatusConfig(r.data || [])).catch(() => {});
  }, []);

  // POLLING svakih 7s — dohvati AI prijedloge koje je backend generirao nakon spremanja bilješke
  useEffect(() => {
    let stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get('/leads/notes/pending');
        if (stop || !Array.isArray(data)) return;
        // SINKRONIZACIJA: što više nije pending (odlučeno u drugoj kartici / isteklo 48h) → makni iz reda.
        // Uklonjene i iz shownRef da se, ako ikad opet postanu pending, mogu ponovno prikazati.
        const pendingIds = new Set(data.map(d => d.id));
        setQueue(q => {
          const gone = q.filter(x => !pendingIds.has(x.id));
          if (!gone.length) return q;
          gone.forEach(x => shownRef.current.delete(x.id));
          return q.filter(x => pendingIds.has(x.id));
        });
        const fresh = data.filter(d => !shownRef.current.has(d.id));
        if (fresh.length) {
          fresh.forEach(d => shownRef.current.add(d.id));
          setQueue(q => [...q, ...fresh]);
        }
      } catch { /* tiho */ }
    };
    poll();
    const iv = setInterval(poll, 7000);
    return () => { stop = true; clearInterval(iv); };
  }, []);

  // Zadržano radi kompatibilnosti — okidanje je sad na backendu, pa je no-op.
  const requestAnalysis = useCallback(() => {}, []);
  // TRAJNO odbacivanje — SAMO preko eksplicitnog gumba "Odbij sve" (svjesna odluka).
  // Popup se više NE može zatvoriti na X / klik izvan — statusi se ne smiju izgubiti.
  const dismiss = useCallback((id) => {
    setQueue(q => q.filter(x => x.id !== id));
    broadcastDecided(id);
    api.post('/leads/notes/reject', { suggestion_id: id }).catch(() => {});
  }, [broadcastDecided]);
  // ODGODA: makni popup s ekrana ali NIŠTA ne šalji serveru (ostaje pending) —
  // prikazuje se čip na dnu stranice, a nakon refresha se vraća (polling ga opet nađe).
  const [snoozedIds, setSnoozedIds] = useState(new Set());
  const snooze = useCallback((id) => setSnoozedIds(p => new Set([...p, id])), []);
  const wakeAll = useCallback(() => setSnoozedIds(new Set()), []);

  return (
    <Ctx.Provider value={{ queue, statusConfig, requestAnalysis, dismiss, applyOptimistic, toast, snoozedIds, snooze, wakeAll }}>
      {children}
    </Ctx.Provider>
  );
}
